import { useState } from "react";
import type { Compaction } from "@/api/queries";
import { Markdown } from "./Markdown";
import { Chip } from "@/components/ui/Chip";
import { ChevronRightIcon, CompressIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * Where the model's view of the conversation begins (PRD FR-3.4).
 *
 * Everything above this line is still in the transcript and still readable —
 * compaction shortens what the *model* is sent, never what the reader can see.
 * The briefing that stands in for those turns is right here and expandable,
 * because "what did it keep?" is the first question anyone asks about a summary
 * that is now driving the answers.
 */
export function CompactionDivider({ compaction }: { compaction: Compaction }) {
  const [open, setOpen] = useState(false);
  const when = compaction.compacted_at ? new Date(compaction.compacted_at) : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <span className="bg-border h-px flex-1" />
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className={cn(
            "type-caption text-ink-secondary hover:text-ink rounded-full px-2 py-1",
            "border-border motion-fast inline-flex items-center gap-1.5 border",
          )}
        >
          <CompressIcon className="size-3" />
          Earlier turns condensed into a briefing
          <ChevronRightIcon className={cn("size-3 motion-fast", open && "rotate-90")} />
        </button>
        <span className="bg-border h-px flex-1" />
      </div>

      {open ? (
        <div className="border-border rounded-container bg-surface flex flex-col gap-2 border p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone="accent">pinned briefing</Chip>
            <span className="type-caption text-ink-disabled">
              covers everything up to turn {compaction.through_seq}
              {when ? ` · taken ${when.toLocaleString()}` : ""}
            </span>
          </div>
          <p className="type-support text-ink-secondary">
            The turns above are still shown in full here. This is what the model is sent in their
            place, so the window carries the facts and figures rather than the transcript.
          </p>
          <div className="text-ink">
            <Markdown>{compaction.summary}</Markdown>
          </div>
        </div>
      ) : null}
    </div>
  );
}
